import { auth, db } from './firebase-config.js';
import { ADMIN_CONFIG, isAdminEmail as checkAdminEmail } from './admin-config.js';

// Update lock to prevent concurrent database updates
// let updateInProgress = null; // Temporarily disabled with email verification

const CycleAuth = {
    async signUp(email, password, fullName) {
        try {
            const userCredential = await auth.createUserWithEmailAndPassword(email, password);
            const user = userCredential.user;
            
            // Create user profile
            await db.collection('users').doc(user.uid).set({
                uid: user.uid,
                email: email,
                fullName: fullName,
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                karmaPoints: 0,
                isHelper: false,
                isAdmin: this.isAdminEmail(email),
                emailVerified: true // TEMPORARILY DISABLED - Set to true by default
            });
            
            // Send email verification
            await user.sendEmailVerification({
                url: window.location.origin,
                handleCodeInApp: false
            });
            
            return { success: true, user, emailVerificationSent: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },
    
    async signIn(email, password) {
        try {
            const userCredential = await auth.signInWithEmailAndPassword(email, password);
            
            // Check if user is admin and update their profile if needed
            // REMOVED: Client-side admin promotion for security
            // Admin status should only be set server-side via Cloud Functions or console
            // if (this.isAdminEmail(email)) {
            //     const userDoc = await db.collection('users').doc(userCredential.user.uid).get();
            //     if (userDoc.exists && !userDoc.data().isAdmin) {
            //         await db.collection('users').doc(userCredential.user.uid).update({
            //             isAdmin: true
            //         });
            //     }
            // }
            
            return { success: true, user: userCredential.user };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },
    
    async signOut() {
        await auth.signOut();
        location.reload();
    },
    
    isAdminEmail(email) {
        return checkAdminEmail(email);
    },
    
    async sendSignInLink(email) {
        try {
            const actionCodeSettings = {
                url: window.location.origin,
                handleCodeInApp: true
            };
            
            await auth.sendSignInLinkToEmail(email, actionCodeSettings);
            window.localStorage.setItem('emailForSignIn', email);
            
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },
    
    async completeSignInWithEmailLink() {
        try {
            if (auth.isSignInWithEmailLink(window.location.href)) {
                let email = window.localStorage.getItem('emailForSignIn');
                if (!email) {
                    email = window.prompt('Please provide your email for confirmation');
                }
                
                const result = await auth.signInWithEmailLink(email, window.location.href);
                window.localStorage.removeItem('emailForSignIn');
                
                // Mark that we just signed in via email link
                sessionStorage.setItem('signedInViaEmailLink', 'true');
                
                // Check if user document exists, if not create it
                const userDoc = await db.collection('users').doc(result.user.uid).get();
                if (!userDoc.exists) {
                    // Create user profile for email link sign-in
                    await db.collection('users').doc(result.user.uid).set({
                        uid: result.user.uid,
                        email: email,
                        fullName: email.split('@')[0], // Default name from email
                        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                        karmaPoints: 0,
                        isHelper: false,
                        isAdmin: this.isAdminEmail(email),
                        emailVerified: true // Email link sign-in verifies the email
                    });
                } else {
                    // Update existing user
                    const updates = {
                        emailVerified: true // Email link sign-in verifies the email
                    };
                    
                    // Also update admin status if needed
                    if (this.isAdminEmail(email) && !userDoc.data().isAdmin) {
                        updates.isAdmin = true;
                    }
                    
                    await db.collection('users').doc(result.user.uid).update(updates);
                }
                
                // Clean up URL
                if (window.history && window.history.replaceState) {
                    window.history.replaceState({}, document.title, window.location.pathname);
                }
                
                return { success: true, user: result.user };
            }
            return { success: false };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },
    
    async resendVerificationEmail() {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('No user logged in');
            }
            
            if (user.emailVerified) {
                return { success: false, error: 'Email already verified' };
            }
            
            await user.sendEmailVerification({
                url: window.location.origin,
                handleCodeInApp: false
            });
            
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },
    
    async checkEmailVerification() {
        // TEMPORARILY DISABLED - Always return true
        return true;
        
        /* eslint-disable no-unreachable */
        // Code below is temporarily disabled but kept for reference
        /*
        try {
            const user = auth.currentUser;
            if (!user) return false;
            
            // Admin users are always considered verified - check this FIRST
            if (this.isAdminEmail(user.email)) {
                return true;
            }
            
            // If there's already an update in progress, wait for it
            if (updateInProgress) {
                return await updateInProgress;
            }
            
            // Create a new update promise
            updateInProgress = this._performEmailVerificationCheck(user);
            
            try {
                const result = await updateInProgress;
                return result;
            } finally {
                updateInProgress = null;
            }
        } catch (error) {
            console.error('Error checking email verification:', error);
            return false;
        }
        */
        /* eslint-enable no-unreachable */
    },
    
    async _performEmailVerificationCheck(user) {
        // Reload user to get latest emailVerified status
        await user.reload();
        
        // Check database for email verification status
        const userDoc = await db.collection('users').doc(user.uid).get();
        if (userDoc.exists) {
            const userData = userDoc.data();
            
            // If database says email is verified, trust that
            if (userData.emailVerified) {
                return true;
            }
        }
        
        // Check if we just came from an email link sign-in
        const justSignedInViaLink = sessionStorage.getItem('signedInViaEmailLink') === 'true';
        if (justSignedInViaLink) {
            sessionStorage.removeItem('signedInViaEmailLink');
            // Only update if not already verified
            if (userDoc.exists && !userDoc.data().emailVerified) {
                await db.collection('users').doc(user.uid).update({
                    emailVerified: true
                });
            }
            return true;
        }
        
        // Update database if Firebase Auth says email is verified
        if (user.emailVerified) {
            // Only update if not already verified in database
            if (userDoc.exists && !userDoc.data().emailVerified) {
                await db.collection('users').doc(user.uid).update({
                    emailVerified: true
                });
            }
            return true;
        }
        
        return false;
    }
};

// Auth state observer
function setupAuthObserver() {
    auth.onAuthStateChanged(async (user) => {
        window.currentUser = user;
        
        // Check email verification status
        if (user) {
            // Force reload user to get latest emailVerified status
            try {
                await user.reload();
            } catch (error) {
                console.log('Could not reload user:', error);
            }
            window.isEmailVerified = await CycleAuth.checkEmailVerification();
        } else {
            window.isEmailVerified = false;
        }
        
        updateUIForAuthState(user);
    });
}

async function updateUIForAuthState(user) {
    const navLinks = document.getElementById('navLinks');
    const emailVerificationBanner = document.getElementById('emailVerificationBanner');
    
    // Always start with banner hidden
    emailVerificationBanner.style.display = 'none';
    
    // Show/hide email verification banner
    if (user) {
        // For admin emails, never show the banner
        if (CycleAuth.isAdminEmail(user.email)) {
            // Admin user - keep banner hidden
            emailVerificationBanner.style.display = 'none';
        } else if (window.isEmailVerified) {
            // User is verified, keep banner hidden
            emailVerificationBanner.style.display = 'none';
        } else {
            // Check if banner was dismissed
            const bannerDismissed = sessionStorage.getItem('emailBannerDismissed') === 'true';
            if (!bannerDismissed) {
                // Not verified - only show banner after we confirm they're really unverified
                const isEmailLinkUrl = auth.isSignInWithEmailLink(window.location.href);
                const justSignedIn = sessionStorage.getItem('signedInViaEmailLink') === 'true';
                
                if (!isEmailLinkUrl && !justSignedIn) {
                    // Not an email link sign-in, show banner
                    emailVerificationBanner.style.display = 'block';
                }
                // For email link sign-ins, keep banner hidden (they're verified by definition)
            }
        }
    }
    
    if (user) {
        // User is signed in
        const userDoc = await db.collection('users').doc(user.uid).get();
        const userData = userDoc.data();
        
        // Check if user signed up with intent to become helper
        if (window.registrationIntent === 'helper') {
            window.registrationIntent = null; // Clear the intent
            setTimeout(() => {
                if (window.openHelperApplication) {
                    window.openHelperApplication(); // Open application form after successful auth
                }
            }, 500); // Small delay to ensure UI is updated
        }
        
        // Check if user is admin
        const isAdmin = checkAdminEmail(user.email);
        
        // Update navigation
        let authButtons = `
            <button class="cta-button" id="dashboardBtn">My Dashboard</button>
        `;
        
        // Add admin link if user is admin
        if (isAdmin) {
            authButtons += '<a href="admin.html" class="btn-secondary" style="padding: 12px 25px; text-decoration: none;">Admin</a>';
        }
        
        authButtons += '<button class="btn-secondary" id="signOutBtn" style="padding: 12px 25px;">Sign Out</button>';
        
        const existingButtons = navLinks.querySelector('.auth-buttons');
        if (existingButtons) {
            existingButtons.innerHTML = authButtons;
        } else {
            const div = document.createElement('div');
            div.className = 'auth-buttons';
            div.style.cssText = 'display: flex; gap: 15px; align-items: center;';
            div.innerHTML = authButtons;
            navLinks.appendChild(div);
        }
        
        // Add event listeners
        document.getElementById('dashboardBtn').addEventListener('click', () => window.openDashboard());
        document.getElementById('signOutBtn').addEventListener('click', () => window.CycleAuth.signOut());
    } else {
        // User is signed out
        const authButtons = `
            <button class="cta-button" id="signInBtn">Sign In</button>
        `;
        
        const existingButtons = navLinks.querySelector('.auth-buttons');
        if (existingButtons) {
            existingButtons.innerHTML = authButtons;
        } else {
            const div = document.createElement('div');
            div.className = 'auth-buttons';
            div.innerHTML = authButtons;
            navLinks.appendChild(div);
        }
        
        // Add event listener
        document.getElementById('signInBtn').addEventListener('click', () => window.openAuthModal());
    }
}

// Make CycleAuth globally accessible
window.CycleAuth = CycleAuth;

export { CycleAuth, setupAuthObserver };